import { createQommerceClient } from "@sinqro/qommerce-sdk";
import {
  buildConsumerRuntimeHeaders,
} from "./runtime-endpoints";
import { persistAndApplyTerminalTheme } from "./terminal-theme";
import { saveMenusToStorage } from "./menu-cache";
import { syncRuntimeLocalization } from "./runtime-localization";
import {
  applyPlatformMetadataToDocument,
  defaultPlatformMetadata,
  fetchPlatformMetadataFromUrl,
  persistPlatformMetadata,
  readPlatformMetadata,
  resolveCurrentHostMetadataUrl,
} from "./platform-metadata";
import { setRuntimeTranslationsBundles } from "./shared/i18n";

const asObject = (value: unknown): Record<string, any> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, any>
    : {};

function readPublishedTerminalConfig(): Record<string, any> {
  try {
    return asObject(JSON.parse(localStorage.getItem("consumerRuntimePublishedTerminalConfig") || "{}"));
  } catch {
    return {};
  }
}

function mergeTerminalConfig(apiValue: unknown): Record<string, any> {
  const api = asObject(apiValue);
  const published = readPublishedTerminalConfig();
  if (!Object.keys(published).length) return api;

  const apiRuntime = asObject(api.consumerRuntime);
  const publishedRuntime = asObject(published.consumerRuntime);
  return {
    ...api,
    ...published,
    theme: { ...asObject(api.theme), ...asObject(published.theme) },
    consumerRuntime: {
      ...apiRuntime,
      ...publishedRuntime,
      theme: { ...asObject(apiRuntime.theme), ...asObject(publishedRuntime.theme) },
      layout: { ...asObject(apiRuntime.layout), ...asObject(publishedRuntime.layout) },
      idleScreen: { ...asObject(apiRuntime.idleScreen), ...asObject(publishedRuntime.idleScreen) },
      typography: { ...asObject(apiRuntime.typography), ...asObject(publishedRuntime.typography) },
      localization: { ...asObject(apiRuntime.localization), ...asObject(publishedRuntime.localization) },
    },
  };
}

export function normalizeBootstrapMenus(raw: unknown): any[] {
  let data = raw as any;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.menus)) return data.menus;
  if (Array.isArray(data?.data?.menus)) return data.data.menus;
  if (Array.isArray(data?.data)) return data.data;
  if (data && typeof data === "object" && Array.isArray((data as any).menuSections)) {
    return [{ id: "menu", name: "Menu", menuSections: (data as any).menuSections }];
  }
  return [];
}

function upsertRuntimeIcon(rel: string, href: string) {
  if (typeof document === "undefined" || !href) return;
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

export function resolveMarketIcon(brand: any, sellingPoint?: any): string {
  const profile = brand && typeof brand === "object" ? brand : {};
  const metadata = profile?.metadata && typeof profile.metadata === "object" ? profile.metadata : {};
  const assets = profile?.assets && typeof profile.assets === "object" ? profile.assets : {};
  return String(
    profile.iconUrl ||
      profile.icon ||
      profile.iconImage ||
      profile.squareLogoUrl ||
      profile.squareLogoImage ||
      assets.iconUrl ||
      assets.icon ||
      assets.iconImage ||
      assets.squareLogoUrl ||
      assets.squareLogoImage ||
      metadata.iconUrl ||
      metadata.icon ||
      metadata.iconImage ||
      metadata.squareLogoUrl ||
      metadata.squareLogoImage ||
      sellingPoint?.iconUrl ||
      sellingPoint?.icon ||
      sellingPoint?.iconImage ||
      sellingPoint?.squareLogoUrl ||
      sellingPoint?.squareLogoImage ||
      sellingPoint?.squareImage ||
      ""
  ).trim();
}

function applyMarketIconToDocument(brand: any, sellingPoint?: any) {
  const icon = resolveMarketIcon(brand, sellingPoint);
  if (!icon) return;
  upsertRuntimeIcon("icon", icon);
  upsertRuntimeIcon("shortcut icon", icon);
  upsertRuntimeIcon("apple-touch-icon", icon);
}

function writeBrandToStorage(brand: any, sellingPoint?: any) {
  const profile = brand && typeof brand === "object" ? brand : {};
  const metadata = profile?.metadata && typeof profile.metadata === "object" ? profile.metadata : {};
  const assets = profile?.assets && typeof profile.assets === "object" ? profile.assets : {};
  const logo =
    assets.logoUrl ||
    assets.logo ||
    metadata.logoUrl ||
    metadata.logo ||
    metadata.brandDarkLogo ||
    sellingPoint?.logoUrl ||
    sellingPoint?.logo ||
    sellingPoint?.squareImage ||
    "";
  const name =
    profile?.restaurantName ||
    profile?.displayName ||
    profile?.name ||
    metadata.restaurantName ||
    metadata.displayName ||
    "Restaurante";
  try {
    localStorage.setItem("restaurantName", String(name || "Restaurante"));
    if (logo) localStorage.setItem("restaurantLogo", String(logo));
    else localStorage.removeItem("restaurantLogo");
  } catch {}
  applyMarketIconToDocument(profile, sellingPoint);
}

function writeRuntimeBackgroundToStorage(kioskConfig: any, restaurantBrand: any, marketplaceBrand: any, sellingPoint?: any) {
  const restaurantProfile = restaurantBrand && typeof restaurantBrand === "object" ? restaurantBrand : {};
  const restaurantMetadata =
    restaurantProfile?.metadata && typeof restaurantProfile.metadata === "object"
      ? restaurantProfile.metadata
      : {};
  const restaurantAssets =
    restaurantProfile?.assets && typeof restaurantProfile.assets === "object"
      ? restaurantProfile.assets
      : {};

  const marketplaceProfile = marketplaceBrand && typeof marketplaceBrand === "object" ? marketplaceBrand : {};
  const marketplaceMetadata =
    marketplaceProfile?.metadata && typeof marketplaceProfile.metadata === "object"
      ? marketplaceProfile.metadata
      : {};
  const marketplaceAssets =
    marketplaceProfile?.assets && typeof marketplaceProfile.assets === "object"
      ? marketplaceProfile.assets
      : {};

  const backgroundImage =
    String(kioskConfig?.kioskBackgroundImage || "").trim() ||
    marketplaceAssets.kioskBackgroundImage ||
    marketplaceAssets.coverImageUrl ||
    marketplaceAssets.backgroundImageUrl ||
    marketplaceMetadata.kioskBackgroundImage ||
    marketplaceMetadata.coverImageUrl ||
    marketplaceMetadata.backgroundImageUrl ||
    restaurantAssets.coverImageUrl ||
    restaurantAssets.backgroundImageUrl ||
    restaurantMetadata.coverImageUrl ||
    restaurantMetadata.backgroundImageUrl ||
    sellingPoint?.mainImageLarge ||
    sellingPoint?.mainImageUrl ||
    sellingPoint?.mainImage ||
    sellingPoint?.imageUrl ||
    "";

  try {
    if (backgroundImage) localStorage.setItem("marketplaceBackgroundImage", String(backgroundImage));
    else localStorage.removeItem("marketplaceBackgroundImage");
    window.dispatchEvent(new CustomEvent("runtime-branding:updated"));
  } catch {}
}

async function refreshPlatformMetadata(pageTitle = "Kiosk") {
  const metadata = await fetchPlatformMetadataFromUrl(resolveCurrentHostMetadataUrl()).catch(() => null);
  if (!metadata) return;
  persistPlatformMetadata(metadata);
  applyPlatformMetadataToDocument(metadata, pageTitle);
}

const bootstrapRefreshes = new Map<string, Promise<{
  payload: any;
  normalizedMenus: any[];
}>>();

async function executeBootstrapRefresh(
  connectionUuid: string,
  options?: {
    language?: string;
    refreshPlatformBranding?: boolean;
    pageTitle?: string;
    preserveStoredLanguage?: boolean;
  }
) {
  const {
    language,
    refreshPlatformBranding = false,
    pageTitle = "Kiosk",
    preserveStoredLanguage = true,
  } = options || {};
  const qommerce = createQommerceClient({
    application: "kiosk",
    environment: import.meta.env.PROD ? "production" : "development",
    timeoutMs: 15_000,
    clientName: "orderinq-front-consumer-runtime",
  });
  const response = await qommerce.getKioskBootstrap<any>(connectionUuid, {
    headers: buildConsumerRuntimeHeaders(connectionUuid, { language }),
  });
  const payload = response?.data || response || {};
  const terminalConfig = mergeTerminalConfig(payload.kioskConfig);
  localStorage.setItem("terminalConfig", JSON.stringify(terminalConfig));
  await syncRuntimeLocalization(terminalConfig, { preserveStoredLanguage }).catch(() => undefined);
  setRuntimeTranslationsBundles(payload.translations || {});
  persistAndApplyTerminalTheme(terminalConfig);
  writeBrandToStorage(payload.restaurantBrand || null, payload.sellingPoint || null);
  writeRuntimeBackgroundToStorage(
    terminalConfig,
    payload.restaurantBrand || null,
    payload.marketplaceBrand || null,
    payload.sellingPoint || null
  );

  if (refreshPlatformBranding) {
    await refreshPlatformMetadata(pageTitle).catch(() => undefined);
    // Platform metadata supplies the safe fallback identity. Once the domain
    // bootstrap is available, the dedicated market icon is authoritative.
    applyMarketIconToDocument(payload.restaurantBrand || null, payload.sellingPoint || null);
  }

  const normalizedMenus = normalizeBootstrapMenus(payload.menus);
  if (normalizedMenus.length > 0) {
    saveMenusToStorage(normalizedMenus, language || "es");
  }

  return {
    payload,
    normalizedMenus,
  };
}

export function refreshBootstrap(
  connectionUuid: string,
  options?: {
    language?: string;
    refreshPlatformBranding?: boolean;
    pageTitle?: string;
    preserveStoredLanguage?: boolean;
  }
) {
  const language = String(options?.language || "es").trim().slice(0, 2).toLowerCase() || "es";
  const key = `${connectionUuid}:${language}:${options?.refreshPlatformBranding === true ? "branding" : "menus"}`;
  const active = bootstrapRefreshes.get(key);
  if (active) return active;

  const request = executeBootstrapRefresh(connectionUuid, { ...options, language })
    .finally(() => bootstrapRefreshes.delete(key));
  bootstrapRefreshes.set(key, request);
  return request;
}

export function restoreStoredPlatformMetadata(pageTitle = "Kiosk") {
  const metadata = readPlatformMetadata() || defaultPlatformMetadata();
  applyPlatformMetadataToDocument(metadata, pageTitle);
}
